/*
 * Every mark in the app, bound to one concept.
 *
 * One mark means one thing. `assertUnique` throws in development if two
 * concepts share a glyph, because a reused mark looks fine in the change that
 * adds it and only becomes confusing three screens later.
 *
 * A mark moves for two reasons only: to show a state it is now in (the
 * chevron turns), or to preview its action (retry winds up, start nudges
 * forward). Nothing moves to be charming.
 */
import {
  Activity, ArrowLeft, ArrowUpRight, BadgeCheck, Ban, Bell, Blocks, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight,
  Archive, ArchiveRestore, Bold, ChartBar, Heading2, ImagePlus, Italic, List, Lock, NotebookPen, Share2, Trash2,
  ChevronsLeft, ChevronsRight, CircleAlert, CircleCheck, CirclePause, CirclePlay, CircleUser, Minus, Pencil,
  ClipboardList, Clock, Copy, Eye, FileVideo, Globe, HardDriveUpload, KeyRound, KeySquare, Layers, Link2, ListChecks,
  LogOut, Megaphone, Monitor, Moon, MoreHorizontal, Palette, PanelLeft, Play, Plus, RotateCw, ScrollText, Server, Settings2,
  Sun, Upload, Users, Wallet, Webhook, X, Download, LayoutGrid, Search, Pin, PinOff,
  Award, Crown, Flame, Gem, Medal, Mountain, Rocket, Shield, Signal, Sprout, Star, Trophy, Zap,
} from 'lucide-react'
import type { Variants } from 'framer-motion'
import { StopMark } from './drawn'
import { fromLucide, type MarkComponent } from './fromLucide'

const SNAP = { type: 'spring', stiffness: 520, damping: 30, mass: 0.5 } as const

const TURN: Variants = { rest: { rotate: 0 }, on: { rotate: 90, transition: SNAP } }
const WIND: Variants = { rest: { rotate: 0 }, hover: { rotate: 38, transition: SNAP } }
const NUDGE: Variants = { rest: { x: 0 }, hover: { x: 1.5, transition: SNAP }, press: { x: 0.5, transition: SNAP } }
const LIFT: Variants = { rest: { y: 0 }, hover: { y: -1.5, transition: SNAP }, press: { y: -0.5, transition: SNAP } }
const OUT: Variants = { rest: { x: 0, y: 0 }, hover: { x: 1, y: -1, transition: SNAP } }
const TURN_SLIGHT: Variants = { rest: { rotate: 0 }, hover: { rotate: 22, transition: SNAP } }
const BACK: Variants = { rest: { x: 0 }, hover: { x: -1.5, transition: SNAP } }

interface Entry {
  mark: MarkComponent
  motion?: Variants
}

const CONCEPTS = {
  // Places
  operations: { mark: Activity },
  providers: { mark: Blocks },
  instance: { mark: Server },
  uploads: { mark: HardDriveUpload },
  tasks: { mark: ClipboardList },
  payments: { mark: Wallet },
  settings: { mark: Settings2, motion: TURN_SLIGHT },
  events: { mark: ScrollText },

  // Actions
  start: { mark: Play, motion: NUDGE },
  stop: { mark: StopMark },
  open: { mark: ArrowUpRight, motion: OUT },
  upload: { mark: Upload, motion: LIFT },
  download: { mark: Download, motion: LIFT },
  spreadsheet: { mark: LayoutGrid },
  retry: { mark: RotateCw, motion: WIND },
  cancel: { mark: Ban },
  approve: { mark: BadgeCheck },
  check: { mark: Check },
  plus: { mark: Plus },
  minus: { mark: Minus },
  complete: { mark: CircleCheck },
  edit: { mark: Pencil },
  activate: { mark: CirclePlay },
  deactivate: { mark: CirclePause },
  review: { mark: Eye },
  copy: { mark: Copy },
  close: { mark: X },
  back: { mark: ArrowLeft, motion: BACK },
  more: { mark: MoreHorizontal },
  bell: { mark: Bell },
  announce: { mark: Megaphone },
  // The writing marks, for the announcement composer.
  bold: { mark: Bold },
  italic: { mark: Italic },
  heading: { mark: Heading2 },
  list: { mark: List },
  image: { mark: ImagePlus },
  poll: { mark: ChartBar },
  note: { mark: NotebookPen },
  trash: { mark: Trash2 },
  archive: { mark: Archive },
  restore: { mark: ArchiveRestore },
  private: { mark: Lock },
  shared: { mark: Share2 },
  sidebar: { mark: PanelLeft },
  pin: { mark: Pin },
  unpin: { mark: PinOff },
  signOut: { mark: LogOut, motion: BACK },

  // Things
  file: { mark: FileVideo },
  error: { mark: CircleAlert },
  time: { mark: Clock },
  user: { mark: CircleUser },
  search: { mark: Search },
  members: { mark: Users },
  link: { mark: Link2 },
  capabilities: { mark: ListChecks },
  environment: { mark: Layers },

  // Provider mechanisms
  mechanismApi: { mark: Webhook },
  mechanismBrowser: { mark: Globe },
  mechanismSession: { mark: KeyRound },
  key: { mark: KeySquare },

  // Disclosure
  disclosure: { mark: ChevronRight, motion: TURN },
  dropdown: { mark: ChevronDown },
  chevronLeft: { mark: ChevronLeft },

  // Dates
  calendar: { mark: Calendar },
  jumpBack: { mark: ChevronsLeft, motion: BACK },
  jumpForward: { mark: ChevronsRight, motion: NUDGE },

  // Levels: the place, and the marks a level may wear (contracts' LEVEL_ICONS).
  levels: { mark: Signal },
  levelSprout: { mark: Sprout },
  levelStar: { mark: Star },
  levelMedal: { mark: Medal },
  levelAward: { mark: Award },
  levelTrophy: { mark: Trophy },
  levelCrown: { mark: Crown },
  levelGem: { mark: Gem },
  levelFlame: { mark: Flame },
  levelRocket: { mark: Rocket },
  levelShield: { mark: Shield },
  levelZap: { mark: Zap },
  levelMountain: { mark: Mountain },

  // Appearance
  appearance: { mark: Palette },
  themeSystem: { mark: Monitor },
  themeLight: { mark: Sun },
  themeDark: { mark: Moon },
} satisfies Record<string, Entry>

export type IconConcept = keyof typeof CONCEPTS

function assertUnique(concepts: Record<string, Entry>) {
  const seen = new Map<MarkComponent, string>()
  for (const [concept, { mark }] of Object.entries(concepts)) {
    const other = seen.get(mark)
    if (other) throw new Error(`[icons] "${concept}" and "${other}" share one mark. One mark, one concept.`)
    seen.set(mark, concept)
  }
}

// Cheap enough to run everywhere, which also means a production build can never ship a collision.
assertUnique(CONCEPTS)

export const Icons = Object.fromEntries(
  Object.entries(CONCEPTS).map(([concept, entry]) => [concept, fromLucide(entry.mark, concept, (entry as Entry).motion)]),
) as { [K in IconConcept]: ReturnType<typeof fromLucide> }
